import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Badge as MUIBadge, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { charAvatar } from 'utils/charAvatar';

const StyledBadge = styled(MUIBadge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

function stringToHslColor(str, s = 30, l = 90) {
  let hash = 0;
  for (let i = 0; i < String(str).length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = String(str).charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function AvatarWithLetterFallback({ children, sx, ...restProps }) {
  return (
    <Avatar
      style={{
        backgroundColor: stringToHslColor(children),
        color: stringToHslColor(children, 80, 33),
        fontSize: '0.875rem',
        ...sx,
      }}
      {...restProps}
    >
      {charAvatar(children)}
    </Avatar>
  );
}

AvatarWithLetterFallback.propTypes = {
  children: PropTypes.string,
  sx: PropTypes.object,
};

function UiAvatar(props) {
  const {
    badge = false,
    badgeContent = false,
    children = '',
    sx,
    ...restProps
  } = props;

  const renderAvatar = () => {
    if (badge) {
      return (
        <StyledBadge
          overlap='circular'
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          variant='dot'
        >
          <AvatarWithLetterFallback sx={sx} {...restProps}>
            {children}
          </AvatarWithLetterFallback>
        </StyledBadge>
      );
    }

    if (badgeContent) {
      return (
        <MUIBadge
          overlap='circular'
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          badgeContent={badgeContent}
        >
          <AvatarWithLetterFallback sx={sx} {...restProps}>
            {children}
          </AvatarWithLetterFallback>
        </MUIBadge>
      );
    }

    return (
      <AvatarWithLetterFallback sx={sx} {...restProps}>
        {children}
      </AvatarWithLetterFallback>
    );
  };

  return (
    <Stack direction='row' spacing={2}>
      {renderAvatar()}
    </Stack>
  );
}

UiAvatar.propTypes = {
  children: PropTypes.string,
  badge: PropTypes.bool,
  badgeContent: PropTypes.bool,
  sx: PropTypes.object,
};

export default memo(UiAvatar);
